import { useApiClient, useFetchImmutable } from '@marageti/z4-lib';
import { CountrySetting, Destination } from '@marageti/z4-sdk/lib/travel';
import { useMemo } from 'react';

const useTRFDestinations = () => {
  const apiClient = useApiClient();
  const {
    data: destinationsList,
    isLoading: isDestinationListLoading,
    error: dError,
  } = useFetchImmutable<Destination[]>(
    apiClient.destinationsClient.getDestinations,
    [{ params: { all: true } }],
    true,
  );
  const {
    data: countrySettings,
    isLoading: isCountrySettingsLoading,
    error: csError,
  } = useFetchImmutable<CountrySetting[]>(
    apiClient.countrySettingsClient.getCountrySettings,
    [],
    true,
  );

  const TRFDestinations = useMemo(() => {
    if (!destinationsList || !countrySettings) return [];
    const sortedDests = destinationsList?.sort((a, b) => a.name.localeCompare(b.name));

    const defaultSetting = countrySettings.find((cs) => cs.isoCode2 === 'Default');
    const defaultMinNights = defaultSetting?.minNights ?? 5;

    const minNightsMap = countrySettings.reduce((acc, cs) => {
      acc[cs.isoCode2] = cs.minNights;
      return acc;
    }, {} as Record<string, number>);

    return sortedDests.map((dest) => {
      let minNights = defaultMinNights;

      if (dest.countries.length > 0) {
        const destinationMinNights = Math.max(
          ...dest.countries.map((country) => minNightsMap[country] || defaultMinNights),
        );
        minNights = Math.max(minNights, destinationMinNights);
      }

      return {
        ...dest,
        minNights,
      };
    });
  }, [destinationsList, countrySettings]);

  return {
    data: TRFDestinations,
    isLoading: isDestinationListLoading || isCountrySettingsLoading,
    error: dError || csError,
  };
};

export default useTRFDestinations;
